import React, { useCallback, useEffect, useState } from 'react';
import type { AdminOverviewRow, AgentDashboard } from '../../../../shared/types';
import type { Ctx } from '../ctx';
import { Icon } from '../icons';
import { euro, hm, thisMonth } from '../format';
import { Avatar, Empty, ErrorBanner, MonthNav, Panel, Pill, ReportDetail, Row, Stat } from '../ui';

interface ListRow {
  clientId: number;
  clientName: string;
  minutes: number;
  amountCents: number;
  commissionCents: number;
  sessionsCount?: number;
  online: boolean;
  roles?: string[];
}

/** Report mensili: per il consulente e il commerciale elenco clienti + dettaglio; per il cliente il proprio riepilogo. */
export function ReportsPage({ ctx }: { ctx: Ctx }) {
  const { api, me } = ctx;
  const param = (ctx.pageParam ?? {}) as { clientId?: number };
  const [month, setMonth] = useState(thisMonth());
  const [rows, setRows] = useState<ListRow[]>([]);
  const [selected, setSelected] = useState<number | null>(param.clientId ?? null);
  const [error, setError] = useState<string | null>(null);
  const role = me.user.role;

  const load = useCallback(async () => {
    if (role === 'admin') {
      const r = await api.get<{ month: string; rows: AdminOverviewRow[] }>(`/api/admin/overview?month=${month}`);
      setRows(
        r.rows.map((x) => ({
          clientId: x.clientId,
          clientName: x.clientName,
          minutes: x.minutes,
          amountCents: x.amountCents,
          commissionCents: x.referralCents + x.managementCents,
          sessionsCount: x.sessionsCount,
          online: x.online,
        })),
      );
    } else if (role === 'agent') {
      const d = await api.get<AgentDashboard>(`/api/agent/dashboard?month=${month}`);
      setRows(
        d.rows.map((x) => ({
          clientId: x.clientId,
          clientName: x.clientName,
          minutes: x.minutes,
          amountCents: x.amountCents,
          commissionCents: x.referralCents + x.managementCents,
          online: x.online,
          roles: [...(x.isReferrer ? ['segnalazione'] : []), ...(x.isManager ? ['gestione'] : [])],
        })),
      );
    }
  }, [api, month, role]);

  useEffect(() => {
    load().catch((e) => setError((e as Error).message));
  }, [load]);

  useEffect(() => {
    if (!selected && rows.length === 1) setSelected(rows[0].clientId);
  }, [rows, selected]);

  if (role === 'client') {
    const clientId = me.user.clientId;
    return (
      <div className="panels single">
        <Panel title="Report" subtitle="Il lavoro fatto per te, mese per mese" right={<MonthNav value={month} onChange={setMonth} className="mt0" />}>
          {clientId ? <ReportDetail api={api} clientId={clientId} month={month} showCommissions={false} /> : <Empty>Utente non collegato a un cliente.</Empty>}
        </Panel>
      </div>
    );
  }

  const totals = rows.reduce(
    (t, r) => ({ minutes: t.minutes + r.minutes, amount: t.amount + r.amountCents, commission: t.commission + r.commissionCents }),
    { minutes: 0, amount: 0, commission: 0 },
  );
  const sel = rows.find((r) => r.clientId === selected) ?? null;

  return (
    <div className="panels">
      <Panel title="Report e fatture" subtitle={role === 'admin' ? 'Ore e importi da riportare in fattura' : 'I clienti che segnali o gestisci'}>
        <ErrorBanner message={error} onClose={() => setError(null)} />
        <MonthNav value={month} onChange={setMonth} />
        {rows.length === 0 ? (
          <Empty icon="list">Nessun cliente nel mese.</Empty>
        ) : (
          rows.map((r) => (
            <Row key={r.clientId} onClick={() => setSelected(r.clientId)} active={selected === r.clientId}>
              <Avatar name={r.clientName} on={r.online} />
              <div className="grow">
                <div className="title">{r.clientName}</div>
                <div className="sub">
                  {hm(r.minutes)}
                  {r.sessionsCount != null ? ` · ${r.sessionsCount} sessioni` : ''}
                  {r.roles?.length ? ` · ${r.roles.join(' + ')}` : ''}
                  {r.commissionCents ? ` · provvigioni ${euro(r.commissionCents)}` : ''}
                </div>
              </div>
              <span className="pill">{euro(role === 'agent' ? r.commissionCents : r.amountCents)}</span>
              <Icon name="chevronRight" size={16} className="muted" />
            </Row>
          ))
        )}
        <div className="stats">
          <Stat value={hm(totals.minutes)} label="ore nel mese" />
          <Stat value={euro(totals.amount)} label="fatturabile" navy={role === 'admin'} />
          <Stat value={euro(totals.commission)} label={role === 'admin' ? 'provvigioni commerciali' : 'tue provvigioni'} navy={role === 'agent'} />
        </div>
      </Panel>

      <Panel
        title={sel ? sel.clientName : 'Dettaglio'}
        subtitle={sel ? 'Dettaglio del mese: copia il riepilogo e incollalo nella fattura' : undefined}
        right={sel?.online ? <Pill tone="good" icon="checkCircle" title="Il consulente sta lavorando per questo cliente adesso">in sessione</Pill> : undefined}
      >
        {sel ? (
          <ReportDetail api={api} clientId={sel.clientId} month={month} showCommissions />
        ) : (
          <Empty icon="list">Seleziona un cliente a sinistra per vedere sessioni, note e importi.</Empty>
        )}
      </Panel>
    </div>
  );
}
